import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Star, Trash2, Briefcase, Bell, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Watchlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/watchlist");
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const removeItem = async (key) => {
    await api.delete(`/watchlist/${encodeURIComponent(key)}`);
    setItems((prev) => prev.filter((i) => i.company_key !== key));
    toast.success("Removed from watchlist");
  };

  const runPings = async () => {
    setPinging(true);
    try {
      const { data } = await api.post("/watchlist/run-pings");
      toast.success(`Ping run · sent ${data.sent}, skipped ${data.skipped}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ping run failed");
    } finally { setPinging(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-eyebrow">Company Watchlist</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Never miss a role</h1>
          <p className="text-gray-600 mt-2 max-w-xl">Star companies from the jobs board. We'll email you the moment they post something new — checked daily at 08:00 UTC.</p>
        </div>
        <button data-testid="run-pings-btn" onClick={runPings} disabled={pinging} className="nb-btn bg-[#eab308] text-black px-4 py-2 inline-flex items-center gap-2">
          {pinging ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Check now
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="nb-card p-10 text-center">
          <Star className="w-10 h-10 mx-auto text-[#eab308]" />
          <p className="font-bold text-lg mt-3">Your watchlist is empty</p>
          <p className="text-gray-600 text-sm mt-1 max-w-md mx-auto">Head to the jobs board and tap the star on any company you'd love to work for. We'll ping you when they post.</p>
          <Link to="/jobs" data-testid="browse-jobs-cta" className="inline-block mt-4 nb-btn bg-[#4338ca] text-white px-5 py-2.5">Browse jobs</Link>
        </div>
      ) : (
        <div data-testid="watchlist-grid" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it) => (
            <div key={it.company_key} className="nb-card p-5 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Star className="w-4 h-4 fill-[#eab308] text-[#eab308]" />
                  <div className="font-bold text-lg truncate">{it.company}</div>
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-3 flex-wrap">
                  <span>added {it.added_at ? new Date(it.added_at).toLocaleDateString() : ""}</span>
                  {it.last_notified_at && <span className="text-[#0caa41] font-bold">Last ping: {new Date(it.last_notified_at).toLocaleDateString()}</span>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Link
                  data-testid={`watchlist-search-${it.company_key}`}
                  to={`/jobs?search=${encodeURIComponent(it.company)}`}
                  className="nb-btn bg-white px-3 py-1.5 text-xs inline-flex items-center gap-1"
                >
                  <Briefcase className="w-3.5 h-3.5" /> See roles
                </Link>
                <button
                  data-testid={`watchlist-remove-${it.company_key}`}
                  onClick={() => removeItem(it.company_key)}
                  className="nb-btn bg-[#ec4899] text-white px-3 py-1.5 text-xs inline-flex items-center gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Unstar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
